import { Auth } from 'aws-amplify'
import axios, { AxiosRequestConfig } from 'axios'

export type BearerAuthorizeOption = 'required' | 'arbitrary' | 'disable'

/**
 * Create new Axios instance.
 * @param option API request option
 * @returns new Axios instance
 */
export default (option: { authorized: BearerAuthorizeOption }) => {
    const instance = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        timeout: 5000,
    })

    instance.interceptors.request.use(async config => {
        await setAuthorizationHeader(config, option.authorized)
        return config
    })

    instance.interceptors.response.use(response => response, error => {
        if (error.response) return Promise.reject(error.response)

        // Handle network error handling to not throw an error
        if (error.request) return Promise.reject(new Error('Network Error'))

        // Handle cancel error
        if (axios.isCancel(error)) return Promise.reject(error)

        // Handle other errors
        return Promise.reject(error)
    })

    return instance
}

const setAuthorizationHeader = async (config: AxiosRequestConfig | RequestInit, option: BearerAuthorizeOption) => {
    if (option === 'disable') return
    try {
        const session = await Auth.currentSession()
        const tokenString = session.getIdToken().getJwtToken()
        const token = tokenString.replaceAll('"', '')
        const headers = config.headers || {}
        config.headers = { ...headers, Authorization: `Bearer ${token}` }
    } catch (error) {
        if (option === 'arbitrary') return
        throw new axios.Cancel('Bearer token is required to send this request.')
    }
}